import React from 'react';
import { useAdsAdvanced } from '../../hooks/useAnalytics';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { 
  ImpressionShareBar, 
  PlacementLegend,
  CoverageHoursStrip,
  KpiTile,
  ErrorState,
  EmptyState 
} from './AnalyticsShared';

export function AdsTab({ range }) {
  const { data, isLoading, error, refetch } = useAdsAdvanced(range);

  if (isLoading) {
    return (
      <div className="space-y-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="animate-pulse space-y-4">
                <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                <div className="h-6 bg-gray-200 rounded w-full"></div>
                <div className="h-6 bg-gray-200 rounded w-full"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  if (!data || !data.has_campaigns) {
    return (
      <EmptyState
        title="No sponsored campaigns"
        description="Create your first sponsored listing campaign to see detailed ad analytics here."
      />
    );
  }

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return '—';
    return `$${(val / 100).toFixed(2)}`;
  };

  const formatNumber = (val) => {
    if (val === null || val === undefined) return '—';
    return val.toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Top KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiTile
          title="Total Spend"
          value={data.total_spend_cents}
          format="currency"
          tooltip="Total amount spent on sponsored listings"
        />
        <KpiTile
          title="Total Orders"
          value={data.total_orders}
          format="number"
          tooltip="Orders attributed to sponsored listings"
        />
        <KpiTile
          title="ROAS"
          value={data.roas}
          format="number"
          suffix="x"
          tooltip="Return on ad spend (Revenue / Spend)"
        />
        <KpiTile
          title="CPA"
          value={data.cpa_cents}
          format="currency"
          tooltip="Cost per acquisition (Spend / Orders)"
        />
      </div>

      {/* Impression Share by Placement */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Impression Share by Placement</CardTitle>
            <PlacementLegend />
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Top Placement */}
          {data.top && (
            <ImpressionShareBar
              label="Top Placement"
              value={data.top.impression_share}
              lostBudget={data.top.lost_is_budget}
              lostRank={data.top.lost_is_rank}
              placement="top"
            />
          )}

          {/* Mid Placement */}
          {data.mid && (
            <ImpressionShareBar
              label="Mid Placement"
              value={data.mid.impression_share}
              lostBudget={data.mid.lost_is_budget}
              lostRank={data.mid.lost_is_rank}
              placement="mid"
            />
          )}

          {/* Overall */}
          <ImpressionShareBar
            label="Overall"
            value={data.overall.impression_share}
            lostBudget={data.overall.lost_is_budget}
            lostRank={data.overall.lost_is_rank}
            placement="top"
          />

          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-900">
              <strong>Impression Share:</strong> Percentage of eligible impressions your ads actually received.
              <br />
              <strong>Lost (Budget):</strong> Impressions lost due to insufficient budget.
              <br />
              <strong>Lost (Rank):</strong> Impressions lost due to lower ad rank/bid.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Coverage Hours */}
      {data.coverage_hours && data.coverage_hours.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Ad Coverage Hours</CardTitle>
          </CardHeader>
          <CardContent>
            <CoverageHoursStrip hours={data.coverage_hours} />
            <p className="mt-4 text-sm text-gray-600">
              Hours when your sponsored listings had active budget and were shown to buyers.
              Better coverage = more opportunity to capture demand.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Performance by Placement */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Placement Stats */}
        {data.top && (
          <Card>
            <CardHeader>
              <CardTitle className="text-blue-600">Top Placement Performance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Impressions</p>
                  <p className="text-2xl font-bold text-gray-900">{formatNumber(data.top.impressions)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Clicks</p>
                  <p className="text-2xl font-bold text-gray-900">{formatNumber(data.top.clicks)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">CTR</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {(data.top.ctr * 100).toFixed(2)}%
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Avg CPC</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(data.top.avg_cpc_cents)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Spend</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(data.top.spend_cents)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Orders</p>
                  <p className="text-2xl font-bold text-gray-900">{formatNumber(data.top.orders)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Mid Placement Stats */}
        {data.mid && (
          <Card>
            <CardHeader>
              <CardTitle className="text-purple-600">Mid Placement Performance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Impressions</p>
                  <p className="text-2xl font-bold text-gray-900">{formatNumber(data.mid.impressions)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Clicks</p>
                  <p className="text-2xl font-bold text-gray-900">{formatNumber(data.mid.clicks)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">CTR</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {(data.mid.ctr * 100).toFixed(2)}%
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Avg CPC</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(data.mid.avg_cpc_cents)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Spend</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(data.mid.spend_cents)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Orders</p>
                  <p className="text-2xl font-bold text-gray-900">{formatNumber(data.mid.orders)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
